import { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import EmptyState from '../../../components/empty-state';
import {
	listProductOptions,
	createOption,
	removeOption,
	createOptionValue,
	removeOptionValue,
} from '../../../utils/queries';

function OptionValues({ option, onChanged }) {
	const [value, setValue] = useState('');
	const [saving, setSaving] = useState(false);

	const handleAdd = async (e) => {
		e.preventDefault();
		setSaving(true);
		await createOptionValue(option.productOptionId, { value });
		setSaving(false);
		setValue('');
		onChanged();
	};

	const handleRemove = async (id) => {
		await removeOptionValue(id);
		onChanged();
	};

	return (
		<div className='ml-4 mt-2'>
			<div className='flex flex-wrap gap-2 mb-2'>
				{option.values.map((v) => (
					<span key={v.productOptionValueId} className='inline-flex items-center gap-1 bg-gray-100 rounded-full px-3 py-1 text-sm'>
						{v.value}
						<button type='button' className='text-gray-400 hover:text-pi-red' onClick={() => handleRemove(v.productOptionValueId)}>
							&times;
						</button>
					</span>
				))}
			</div>
			<form onSubmit={handleAdd} className='flex items-center gap-2'>
				<input
					className='form-input w-40 h-8'
					placeholder='New value'
					required
					value={value}
					onChange={(e) => setValue(e.target.value)}
				/>
				<button type='submit' disabled={saving} className='btn-secondary-small'>
					Add Value
				</button>
			</form>
		</div>
	);
}

export default function OptionsTab({ product }) {
	const [options, setOptions] = useState([]);
	const [loading, setLoading] = useState(true);
	const [name, setName] = useState('');
	const [saving, setSaving] = useState(false);
	const [formOpen, setFormOpen] = useState(false);

	const refresh = () => {
		setLoading(true);
		return listProductOptions(product.productId).then((result) => {
			if (result?.success) setOptions(result.data);
			setLoading(false);
		});
	};

	useEffect(() => {
		refresh();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [product.productId]);

	const handleAddOption = async (e) => {
		e.preventDefault();
		setSaving(true);
		await createOption(product.productId, { name, position: options.length + 1 });
		setSaving(false);
		setName('');
		setFormOpen(false);
		refresh();
	};

	const handleRemoveOption = async (id) => {
		await removeOption(id);
		refresh();
	};

	if (loading) return <div className='text-sm text-gray-400 py-4'>Loading...</div>;

	return (
		<div className='max-w-xl'>
			{options.map((option) => (
				<div key={option.productOptionId} className='border-b border-gray-100 py-4'>
					<div className='flex items-center justify-between'>
						<span className='font-medium text-sm'>{option.name}</span>
						<button
							type='button'
							className='text-xs text-gray-400 hover:text-pi-red'
							onClick={() => handleRemoveOption(option.productOptionId)}
						>
							Remove option
						</button>
					</div>
					<OptionValues option={option} onChanged={refresh} />
				</div>
			))}
			{options.length === 0 && !formOpen && (
				<EmptyState
					heading={
						<>
							No options yet (e.g. Color, Size){' '}
							<button type='button' className='underline text-black hover:no-underline' onClick={() => setFormOpen(true)}>
								Add an option
							</button>
						</>
					}
					size='sm'
				/>
			)}

			{!formOpen && options.length > 0 && (
				<button
					type='button'
					className='btn-secondary-small mt-5 rounded-full p-1.5'
					aria-label='Add'
					onClick={() => setFormOpen(true)}
				>
					<Plus className='size-4' />
				</button>
			)}

			{formOpen && (
				<form onSubmit={handleAddOption} className='flex items-end gap-3 mt-5'>
					<div className='flex flex-col'>
						<label>New Option Name</label>
						<input className='form-input w-48' required value={name} onChange={(e) => setName(e.target.value)} />
					</div>
					<button type='submit' disabled={saving} className='btn-primary h-10'>
						{saving ? 'Adding...' : 'Add Option'}
					</button>
					<button
						type='button'
						className='btn-secondary h-10'
						onClick={() => {
							setFormOpen(false);
							setName('');
						}}
					>
						Cancel
					</button>
				</form>
			)}
		</div>
	);
}
